import { FC, ReactNode, useMemo } from 'react';
import classNames from 'classnames';
import swiperCss from '@edikk-edikk/country-common/css/modules/swiper.module.scss';
import { AdvertPhotoType } from '@edikk-edikk/country-common';
import { useBotDetectorContext } from '@edikk-edikk/country-frontend';
import { useAdvertDetailContext } from '#advertDetail/hooks';
import galleryItemCss from '../../assets/gallery/gallery-item.module.scss';

type PropsType = {
  photo: AdvertPhotoType;
  index: number;
};

const GalleryItem: FC<PropsType> = ({
  photo,
  index,
}) => {
  const { isBot } = useBotDetectorContext();
  const {
    isSlave,
    advert: {
      isDeleted,
    },
  } = useAdvertDetailContext();
  const withBlur = useMemo(() => !isBot && isDeleted, [isBot, isDeleted]);

  const getAlt = (): string|null => {
    if (!photo.alt) {
      return null;
    }

    if (index === 0) {
      return photo.alt;
    }

    return `${photo.alt} - ${index + 1}`;
  };

  const renderSource = (media: string, srcSet: string[]): ReactNode => {
    if (srcSet.length === 0) {
      return null;
    }

    const srcSetAsString = srcSet.join(', ');
    return <source media={media} srcSet={srcSetAsString} />;
  };

  const renderSourceForLargeScreen = (): ReactNode => {
    if (!photo) {
      return null;
    }

    if (!photo.preview800Url || !photo.preview1000Url || !photo.url) {
      return null;
    }

    const srcSet = [`${photo.preview800Url} 1x`, `${photo.preview1000Url} 2x`, `${photo.url} 3x`];

    return renderSource('(min-width: 800px)', srcSet);
  };

  const renderSourceForSmallScreen = (): ReactNode => {
    if (!photo) {
      return null;
    }

    if (!photo.preview500Url || !photo.preview800Url || !photo.preview1000Url) {
      return null;
    }

    const srcSet = [`${photo.preview500Url} 1x`, `${photo.preview800Url} 2x`, `${photo.preview1000Url} 3x`];

    return renderSource('(max-width: 799px)', srcSet);
  };

  return (
    <div
      className={classNames(
        swiperCss.swiper__slide,
        galleryItemCss.galleryItem,
        { [galleryItemCss.galleryItem_borderTopRadius]: isSlave },
      )}
    >
      <picture>
        {renderSourceForLargeScreen()}
        {renderSourceForSmallScreen()}
        <img
          className={classNames(
            galleryItemCss.galleryItem__image,
            { [galleryItemCss.galleryItem__image_withBlur]: withBlur },
          )}
          src={photo?.url}
          alt={getAlt()}
          loading={index === 0 ? null : 'lazy'}
          fetchPriority={index === 0 ? 'high' : null}
        />
      </picture>
    </div>
  );
};

export { GalleryItem };
